/**
 * NotificationBell — Icono de campana con badge de no leídas + dropdown.
 */
import { useState, useRef } from 'react'
import { useCierreAlClicAfuera } from '@/lib/useCierreAlClicAfuera'
import { useNavigate } from 'react-router-dom'
import { Bell, BellRing, CheckCheck, FileCheck, FileX, AlertTriangle, RefreshCw, ShieldAlert, CreditCard, AlertCircle, ShoppingCart, ArrowRight, List } from 'lucide-react'
import { usePushNotifications } from '@/hooks/usePushNotifications'
import {
  useUnreadCount,
  useNotifications,
  useMarkRead,
  useMarkAllRead,
  type NotificationEventType,
} from '../hooks/useNotifications'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const EVENT_ICON: Partial<Record<string, React.ElementType>> = {
  document_accepted:    FileCheck,
  document_rejected:    FileX,
  document_error:       AlertTriangle,
  recurring_generated:  RefreshCw,
  certificate_expiring: ShieldAlert,
  payment_confirmed:    CreditCard,
  system_error:         AlertCircle,
  sales_order_created:  ShoppingCart,
}

const EVENT_COLOR: Partial<Record<string, string>> = {
  document_accepted:    'text-success-content-soft bg-success-surface',
  document_rejected:    'text-danger-content-soft bg-danger-surface',
  document_error:       'text-orange-600 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/30',
  recurring_generated:  'text-primary bg-primary-50',
  certificate_expiring: 'text-warning-content-soft bg-warning-surface',
  payment_confirmed:    'text-success-content-soft bg-success-surface',
  system_error:         'text-danger-content-soft bg-danger-surface',
  sales_order_created:  'text-primary bg-primary-50',
}

function timeAgo(isoDate: string): string {
  const diffMs = Date.now() - new Date(isoDate).getTime()
  const mins = Math.floor(diffMs / 60_000)
  if (mins < 1) return 'ahora mismo'
  if (mins < 60) return `hace ${mins} min`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `hace ${hours}h`
  const days = Math.floor(hours / 24)
  return `hace ${days}d`
}

// ─── Component ────────────────────────────────────────────────────────────────

export function NotificationBell() {
  const [open, setOpen] = useState(false)
  const disparadorRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  useCierreAlClicAfuera(open, [disparadorRef, panelRef], () => setOpen(false))
  const navigate = useNavigate()
  const { data: unreadCount = 0 } = useUnreadCount()
  const { data, isLoading } = useNotifications()
  const markRead = useMarkRead()
  const markAllRead = useMarkAllRead()

  const notifications = data?.notifications.slice(0, 10) ?? []

  const handleMarkRead = (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    markRead.mutate(id)
  }

  const handleMarkAll = (e: React.MouseEvent) => {
    e.stopPropagation()
    markAllRead.mutate()
  }

  const handleNotifClick = (notif: { id: string; is_read: boolean; action_url?: string }) => {
    if (!notif.is_read) markRead.mutate(notif.id)
    if (notif.action_url) {
      setOpen(false)
      navigate(notif.action_url)
    }
  }

  return (
    <div className="relative">
      <button ref={disparadorRef}
        onClick={() => setOpen(p => !p)}
        className="relative p-2 text-content-muted hover:text-content hover:bg-soft rounded-lg transition-colors"
        title="Notificaciones"
        aria-label={unreadCount > 0 ? `Notificaciones (${unreadCount} sin leer)` : 'Notificaciones'}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 flex items-center justify-center text-[10px] font-bold bg-danger-solid text-white rounded-full px-0.5">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <>

          {/* Dropdown */}
          <div ref={panelRef}
            className="absolute right-0 top-11 z-50 w-80 bg-popover border border-divider rounded-xl shadow-lg overflow-hidden"
            style={{ backgroundColor: 'rgb(var(--color-popover))' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-divider">
              <p className="text-sm font-semibold text-content">Notificaciones</p>
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAll}
                  className="inline-flex items-center gap-1 text-xs text-content-muted hover:text-content"
                >
                  <CheckCheck size={13} />Marcar todas leídas
                </button>
              )}
            </div>

            {/* Lista */}
            <div className="max-h-96 overflow-y-auto">
              {isLoading ? (
                <div className="py-8 text-center text-sm text-content-subtle">Cargando...</div>
              ) : notifications.length === 0 ? (
                <div className="py-8 text-center text-sm text-content-subtle">
                  <Bell size={24} className="mx-auto mb-2 opacity-30" />
                  Sin notificaciones
                </div>
              ) : (
                <div className="divide-y divide-divider">
                  {notifications.map(notif => {
                    const Icon = EVENT_ICON[notif.event_type] ?? Bell
                    const colorCls = EVENT_COLOR[notif.event_type] ?? 'text-content-muted bg-surface-muted'
                    const clickable = !!notif.action_url
                    const listUrl = typeof notif.metadata?.list_url === 'string'
                      ? (notif.metadata.list_url as string) : ''
                    const listLabel = typeof notif.metadata?.list_label === 'string'
                      ? (notif.metadata.list_label as string) : 'Ir al listado'
                    const handleListClick = (e: React.MouseEvent) => {
                      e.stopPropagation()
                      if (!notif.is_read) markRead.mutate(notif.id)
                      setOpen(false)
                      navigate(listUrl)
                    }
                    return (
                      <div
                        key={notif.id}
                        onClick={clickable ? () => handleNotifClick(notif) : undefined}
                        role={clickable ? 'button' : undefined}
                        tabIndex={clickable ? 0 : undefined}
                        onKeyDown={clickable ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            handleNotifClick(notif)
                          }
                        } : undefined}
                        className={`flex gap-3 px-4 py-3 transition-colors ${
                          notif.is_read ? 'bg-surface' : 'bg-primary-50/40'
                        } ${clickable ? 'cursor-pointer hover:bg-soft focus:outline-none focus:ring-2 focus:ring-primary/40' : ''}`}
                      >
                        {/* Ícono */}
                        <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${colorCls}`}>
                          <Icon size={14} />
                        </div>

                        {/* Contenido */}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-content leading-snug">{notif.title}</p>
                          <p className="text-xs text-content-muted mt-1 leading-relaxed whitespace-pre-line break-words">{notif.message}</p>
                          <div className="mt-2 flex items-center flex-wrap gap-x-3 gap-y-1">
                            <p className="text-[10px] text-content-subtle">{timeAgo(notif.created_at)}</p>
                            {clickable && (
                              <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-primary">
                                Ver detalle <ArrowRight size={10} />
                              </span>
                            )}
                            {listUrl && (
                              <button
                                type="button"
                                onClick={handleListClick}
                                className="inline-flex items-center gap-0.5 text-[10px] font-medium text-content-muted hover:text-primary transition-colors"
                              >
                                <List size={10} /> {listLabel}
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Punto / marcar leída */}
                        <div className="shrink-0 flex items-start pt-1">
                          {!notif.is_read && (
                            <button
                              onClick={e => handleMarkRead(notif.id, e)}
                              className="w-2 h-2 rounded-full bg-primary hover:bg-primary-hover transition-colors"
                              title="Marcar como leída"
                              aria-label="Marcar notificación como leída"
                            />
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Push toggle */}
            <PushToggle />
          </div>
        </>
      )}
    </div>
  )
}


function PushToggle() {
  const { isSupported, isSubscribed, permission, subscribe } = usePushNotifications()

  // No mostrar si no es soportado, ya está suscrito, o el permiso fue bloqueado
  if (!isSupported || isSubscribed || permission === 'denied') return null

  return (
    <div className="px-4 py-2.5 border-t border-divider flex items-center justify-between">
      <div className="flex items-center gap-2 text-xs text-content-muted">
        <BellRing size={13} />
        Recibir notificaciones push
      </div>
      <button
        onClick={subscribe}
        className="text-xs px-2.5 py-1 rounded-full font-medium bg-primary-50 text-primary-hover hover:bg-primary-100 transition-colors"
      >
        Activar
      </button>
    </div>
  )
}
